import {
  ElectricalType as ElectricalTypeProto,
  ModelAnalyticsByElectricalType,
  ModelAnalytics as ModelAnalyticsProto,
} from '@tapestry-energy/protos-platform/commonjs/tap/platform/model_analytics/model_analytics_pb';

import {CimType} from 'frontend/cimclient/graphql/generated/CimSchema';
import {Iri} from 'frontend/utils/rdf_model/tapestry_iri';

import {Voltage} from '../units/voltage';

interface ModelAnalyticsData {
  modelName: string;
  entitiesByType: EntitiesByType;
  entitiesByTypeAndVoltageLevel: EntitiesByTypeAndVoltageLevel;
  entitiesByTypeAndFeeder: EntitiesByTypeAndFeeder;
}

type EntitiesByType = Map<ElectricalType, number>;
type EntitiesByTypeAndVoltageLevel = Map<ElectricalType, Array<[number, number]>>;
type EntitiesByTypeAndFeeder = Map<ElectricalType, Array<[{id: string; name: string}, number]>>;

export enum ElectricalType {
  UNKNOWN = 'Unknown',
  GENERATOR = 'Generators',
  TRANSMISSION_LINE = 'Lines',
  SUBSTATION = 'Substations',
  TRANSFORMER = 'Transformers',
  CAPACITOR_BANKS = 'Capacitor banks',
  REACTOR = 'Reactors',
  VOLTAGE_REGULATOR = 'Voltage regulators',
  ENERGY_STORAGE = 'Energy storage systems',
  TOWERS = 'Towers',
  FEEDERS = 'Feeders',
  REDUCED_BUS = 'Reduced buses',
  REDUCED_BRANCH = 'Reduced branches',
  SWITCH = 'Switch',
  FUSE = 'Fuse',
  BREAKER = 'Breaker',
  RECLOSER = 'Recloser',
  // Technically not an electrical type,
  // but added here to maintain the general
  // architecture as an experimental support
  // for GridAware data inside GPT-D.
  GA_INCIDENT = 'Incidents',
}

export enum ElectricalTypesIcon {
  TOWERS = 'asset-towers',
  GENERATOR = 'asset-generators',
  TRANSMISSION_LINE = 'asset-transmission-lines',
  SUBSTATION = 'asset-substations',
  TRANSFORMER = 'asset-transformers',
  CAPACITOR_BANKS = 'asset-capacitor-banks',
  REACTOR = 'asset-reactors',
  VOLTAGE_REGULATOR = 'asset-voltage-regulators',
  ENERGY_STORAGE = 'asset-energy-storages',
  FEEDER = 'asset-feeders',
  SWITCH = 'asset-switch',
  FUSE = 'asset-fuse',
  BREAKER = 'asset-breaker',
  RECLOSER = 'asset-recloser',
}

export const ICONS_BY_ELECTRICAL_TYPE = new Map([
  [ElectricalType.TOWERS, ElectricalTypesIcon.TOWERS],
  [ElectricalType.GENERATOR, ElectricalTypesIcon.GENERATOR],
  [ElectricalType.TRANSMISSION_LINE, ElectricalTypesIcon.TRANSMISSION_LINE],
  [ElectricalType.SUBSTATION, ElectricalTypesIcon.SUBSTATION],
  [ElectricalType.TRANSFORMER, ElectricalTypesIcon.TRANSFORMER],
  [ElectricalType.CAPACITOR_BANKS, ElectricalTypesIcon.CAPACITOR_BANKS],
  [ElectricalType.REACTOR, ElectricalTypesIcon.REACTOR],
  [ElectricalType.VOLTAGE_REGULATOR, ElectricalTypesIcon.VOLTAGE_REGULATOR],
  [ElectricalType.ENERGY_STORAGE, ElectricalTypesIcon.ENERGY_STORAGE],
  [ElectricalType.FEEDERS, ElectricalTypesIcon.FEEDER],
  [ElectricalType.SWITCH, ElectricalTypesIcon.SWITCH],
  [ElectricalType.FUSE, ElectricalTypesIcon.FUSE],
  [ElectricalType.BREAKER, ElectricalTypesIcon.BREAKER],
  [ElectricalType.RECLOSER, ElectricalTypesIcon.RECLOSER],
  [ElectricalType.REDUCED_BUS, ElectricalTypesIcon.ENERGY_STORAGE],
  [ElectricalType.REDUCED_BRANCH, ElectricalTypesIcon.ENERGY_STORAGE],
]);

export const ELECTRICAL_TYPES = [
  ElectricalType.TOWERS,
  ElectricalType.GENERATOR,
  ElectricalType.TRANSMISSION_LINE,
  ElectricalType.SUBSTATION,
  ElectricalType.TRANSFORMER,
  ElectricalType.CAPACITOR_BANKS,
  ElectricalType.REACTOR,
  ElectricalType.VOLTAGE_REGULATOR,
  ElectricalType.ENERGY_STORAGE,
  ElectricalType.FEEDERS,
  ElectricalType.SWITCH,
  ElectricalType.FUSE,
  ElectricalType.BREAKER,
  ElectricalType.RECLOSER,
  ElectricalType.REDUCED_BUS,
  ElectricalType.REDUCED_BRANCH,
];

/**
 * Mapping between electrical type and cim type.
 * List of cim types grouped by electrical type.
 */
const ELECTRICAL_TYPE_TO_CIM_TYPES = new Map<ElectricalType, CimType[]>([
  [
    ElectricalType.GENERATOR,
    [
      // Plant types.
      CimType.HydroPowerPlant,
      // Generating unit types.
      CimType.GeneratingUnit,
      CimType.SolarGeneratingUnit,
      CimType.ThermalGeneratingUnit,
      CimType.HydroGeneratingUnit,
      CimType.WindGeneratingUnit,
      CimType.PowerElectronicsWindUnit,
      CimType.PhotoVoltaicUnit,
    ],
  ],
  [ElectricalType.TOWERS, []],
  [ElectricalType.TRANSMISSION_LINE, [CimType.AcLineSegment]],
  [ElectricalType.SUBSTATION, [CimType.Substation]],
  [ElectricalType.TRANSFORMER, [CimType.PowerTransformer]],
  [
    ElectricalType.CAPACITOR_BANKS,
    [CimType.LinearShuntCompensator, CimType.NonlinearShuntCompensator],
  ],
  [ElectricalType.REACTOR, []],
  [ElectricalType.VOLTAGE_REGULATOR, [CimType.RegulatingCondEq]],
  [ElectricalType.ENERGY_STORAGE, [CimType.BatteryUnit]],
  [ElectricalType.FEEDERS, [CimType.Feeder]],
  [
    ElectricalType.SWITCH,
    [CimType.Switch, CimType.Disconnector, CimType.GroundDisconnector, CimType.LoadBreakSwitch],
  ],
  [ElectricalType.FUSE, [CimType.Fuse]],
  [ElectricalType.BREAKER, [CimType.Breaker]],
  [ElectricalType.RECLOSER, [CimType.Recloser, CimType.Sectionaliser]],
  [ElectricalType.REDUCED_BUS, [CimType.EquivalentShunt]],
  [ElectricalType.REDUCED_BRANCH, [CimType.EquivalentBranch]],
  // Doesn't really have Cim type, so this
  // is just a close enough equivalent.
  [ElectricalType.GA_INCIDENT, [CimType.ActivityRecord]],
]);

/**
 * Mapping between cim type and electrical type.
 */
const CIM_TYPE_TO_ELECTRICAL_TYPE = new Map<CimType, ElectricalType>(
  [...ELECTRICAL_TYPE_TO_CIM_TYPES.entries()].flatMap(([electricalType, cimTypes]) =>
    cimTypes.map((cimType: CimType) => [cimType, electricalType]),
  ),
);

/**
 * Represents model analytics data, including counts of entities by electrical type, voltage level, and feeder.
 */
export class ModelAnalytics {
  readonly modelName: string;
  private readonly entitiesByType: EntitiesByType;
  private readonly entitiesByTypeAndVoltageLevel: EntitiesByTypeAndVoltageLevel;
  private readonly entitiesByTypeAndFeeder: EntitiesByTypeAndFeeder;

  constructor(data: ModelAnalyticsData) {
    this.modelName = data.modelName;
    this.entitiesByType = data.entitiesByType;
    this.entitiesByTypeAndVoltageLevel = data.entitiesByTypeAndVoltageLevel;
    this.entitiesByTypeAndFeeder = data.entitiesByTypeAndFeeder;
  }

  static fromProto(proto: ModelAnalyticsProto): ModelAnalytics {
    const entitiesByType: EntitiesByType = new Map();
    const entitiesByTypeAndVoltageLevel: EntitiesByTypeAndVoltageLevel = new Map();
    const entitiesByTypeAndFeeder: EntitiesByTypeAndFeeder = new Map();

    for (const entity of proto.getAnalyticsByElectricalType()?.getEntitiesByTypeList() || []) {
      const type = fromElectricalTypeProto(entity.getType());
      entitiesByType.set(type, entity.getCount());
      entitiesByTypeAndVoltageLevel.set(
        type,
        entity
          .getCountByBaseVoltageList()
          .map((byBaseVoltage: ModelAnalyticsByElectricalType.ByBaseVoltage) => [
            Voltage.fromProto(byBaseVoltage.getBaseVoltage()).kiloVolts,
            byBaseVoltage.getCount(),
          ]),
      );
      entitiesByTypeAndFeeder.set(
        type,
        entity.getCountByFeederList().map((byFeeder: ModelAnalyticsByElectricalType.ByFeeder) => [
          {
            id: Iri.fromProto(byFeeder.getFeederIri()).toString(),
            name: byFeeder.getFeederDisplayName(),
          },
          byFeeder.getCount(),
        ]),
      );
    }

    return new ModelAnalytics({
      modelName: proto.getModelName(),
      entitiesByType,
      entitiesByTypeAndVoltageLevel,
      entitiesByTypeAndFeeder,
    });
  }

  getEntitiesCount(type: ElectricalType): number {
    return this.entitiesByType.get(type) || 0;
  }

  getEntitiesCountByVoltageLevel(type: ElectricalType): Array<[number, number]> {
    return this.entitiesByTypeAndVoltageLevel.get(type) || [];
  }

  getEntitiesCountByFeeder(type: ElectricalType): Array<[{id: string; name: string}, number]> {
    return this.entitiesByTypeAndFeeder.get(type) || [];
  }
}

/**
 * Returns the list of cim types that corresponds to the given electrical types.
 */
export function electricalTypesToCimTypes(electricalTypes: ElectricalType[]): CimType[] {
  return electricalTypes.flatMap(
    (electricalType: ElectricalType) => ELECTRICAL_TYPE_TO_CIM_TYPES.get(electricalType) || [],
  );
}

/**
 * Returns electrical type that corresponds to the given cim type.
 */
export function cimTypeToElectricalType(cimType: CimType): ElectricalType | undefined {
  return CIM_TYPE_TO_ELECTRICAL_TYPE.get(cimType);
}

function fromElectricalTypeProto(proto: ElectricalTypeProto): ElectricalType {
  switch (proto) {
    case ElectricalTypeProto.ELECTRICAL_TYPE_GENERATOR:
      return ElectricalType.GENERATOR;
    case ElectricalTypeProto.ELECTRICAL_TYPE_TRANSMISSION_LINE:
      return ElectricalType.TRANSMISSION_LINE;
    case ElectricalTypeProto.ELECTRICAL_TYPE_SUBSTATION:
      return ElectricalType.SUBSTATION;
    case ElectricalTypeProto.ELECTRICAL_TYPE_TRANSFORMER:
      return ElectricalType.TRANSFORMER;
    case ElectricalTypeProto.ELECTRICAL_TYPE_CAPACITOR_BANK:
      return ElectricalType.CAPACITOR_BANKS;
    case ElectricalTypeProto.ELECTRICAL_TYPE_REACTOR:
      return ElectricalType.REACTOR;
    case ElectricalTypeProto.ELECTRICAL_TYPE_ENERGY_STORAGE:
      return ElectricalType.ENERGY_STORAGE;
    case ElectricalTypeProto.ELECTRICAL_TYPE_VOLTAGE_REGULATOR:
      return ElectricalType.VOLTAGE_REGULATOR;
    case ElectricalTypeProto.ELECTRICAL_TYPE_TOWERS:
      return ElectricalType.TOWERS;
    case ElectricalTypeProto.ELECTRICAL_TYPE_FEEDERS:
      return ElectricalType.FEEDERS;
    case ElectricalTypeProto.ELECTRICAL_TYPE_REDUCED_BUS:
      return ElectricalType.REDUCED_BUS;
    case ElectricalTypeProto.ELECTRICAL_TYPE_REDUCED_BRANCH:
      return ElectricalType.REDUCED_BRANCH;
    case ElectricalTypeProto.ELECTRICAL_TYPE_SWITCH:
      return ElectricalType.SWITCH;
    case ElectricalTypeProto.ELECTRICAL_TYPE_FUSE:
      return ElectricalType.FUSE;
    case ElectricalTypeProto.ELECTRICAL_TYPE_BREAKER:
      return ElectricalType.BREAKER;
    case ElectricalTypeProto.ELECTRICAL_TYPE_RECLOSER:
      return ElectricalType.RECLOSER;
    default:
      return ElectricalType.UNKNOWN;
  }
}
